import {
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import ApiCredentials from "entities/ApiCredentials";
import { get } from "lodash";
import moment from "moment";
import omitDeep from "omit-deep-lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdAutorenew,
  MdOutlineDeleteOutline,
  MdSave,
  MdWarningAmber,
} from "react-icons/md";
import Button from "shared/components/Button";
import Select from "shared/components/Select";
import Tooltip from "shared/components/Tooltip";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import SecretFields from "../../ApiCredential/SecretFields";
import { isValidIpRule } from "utils/helpers/validations";

const { Text } = Typography;

const CredentialsTable = ({ data = [], refetch }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.BROKER);
  const CREDENTIALS = t("API_CREDENTIALS", { returnObjects: true });

  const [drafts, setDrafts] = useState({});

  const [update, { loading }] = useMutation(
    graphql.mutations.UPDATE_API_CREDENTIAL,
    {
      onCompleted: () => {
        notification.success({ message: CREDENTIALS.SAVED });
        if (refetch) refetch();
      },
    },
  );

  const credentials = omitDeep(data, "__typename");

  const acceptIpEntries = (entries) => {
    const accepted = [];
    (entries || []).forEach((entry) => {
      const trimmed = String(entry).trim();
      if (!trimmed || accepted.includes(trimmed)) return;
      if (!isValidIpRule(trimmed)) {
        notification.warning({
          message: `${trimmed} — ${CREDENTIALS.INVALID_IP}`,
        });
        return;
      }
      accepted.push(trimmed);
    });
    return accepted;
  };

  const saveIps = (id, allowedIps) =>
    update({ variables: { where: { id }, data: { allowedIps } } });

  const [rotated, setRotated] = useState(null);

  const [rotate, { loading: rotating }] = useMutation(
    graphql.mutations.ROTATE_API_CREDENTIAL_SECRET,
    {
      onCompleted: (data) => setRotated(get(data, "rotateApiCredentialSecret")),
    },
  );

  const [remove, { loading: removing }] = useMutation(
    graphql.mutations.DELETE_API_CREDENTIAL,
    {
      onCompleted: () => {
        notification.success({ message: CREDENTIALS.DELETED });
        if (refetch) refetch();
      },
    },
  );

  const columns = [
    {
      title: CREDENTIALS.PARTNER,
      dataIndex: "label",
      render: (label, record) => (
        <div className="api-credential__stack">
          <Text strong>{label}</Text>
        </div>
      ),
    },
    {
      title: CREDENTIALS.ALLOWED_IPS,
      dataIndex: "allowedIps",
      width: 420,
      render: (allowedIps, record) => {
        const id = get(record, "id");
        const current = allowedIps || [];
        const draft = drafts[id] !== undefined ? drafts[id] : current;
        const dirty = draft.join(",") !== current.join(",");
        return (
          <div className="api-credential__ips">
            <div className="api-credential__ips-row">
              <Select
                type="tag"
                mode="tags"
                options={[]}
                notFoundContent={null}
                suffixIcon={null}
                tokenSeparators={[",", " "]}
                placeholder={CREDENTIALS.ALLOWED_IPS_PLACEHOLDER}
                value={draft}
                onChange={(entries) =>
                  setDrafts((previous) => ({
                    ...previous,
                    [id]: acceptIpEntries(entries),
                  }))
                }
              />
              <Button
                className="api-credential__ips-save"
                type="primary--outlined"
                disabled={!dirty || loading}
                icon={<MdSave size={18} />}
                onClick={() => saveIps(id, draft)}
              >
                {CREDENTIALS.SAVE}
              </Button>
            </div>
            {!current.length && (
              <Tooltip title={CREDENTIALS.NO_IP_TOOLTIP}>
                <Tag icon={<MdWarningAmber />} color="warning">
                  {CREDENTIALS.NO_IP_RESTRICTION}
                </Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: CREDENTIALS.USAGE,
      dataIndex: "usageCount",
      align: "right",
      render: (usageCount, record) => (
        <div className="api-credential__stack api-credential__stack--end">
          <Text strong>{usageCount || 0}</Text>
          {get(record, "lastUsedAt") ? (
            <Text type="secondary" className="api-credential__meta">
              {moment(get(record, "lastUsedAt")).format("DD/MM/YYYY HH:mm")}
              {get(record, "lastUsedIp")
                ? ` · ${get(record, "lastUsedIp")}`
                : ""}
            </Text>
          ) : (
            <Text type="secondary" className="api-credential__meta">
              {CREDENTIALS.NEVER_USED}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: CREDENTIALS.STATUS,
      dataIndex: "enabled",
      align: "right",
      render: (enabled, record) => (
        <Switch
          checked={Boolean(enabled)}
          loading={loading}
          onChange={(checked) =>
            update({
              variables: {
                where: { id: get(record, "id") },
                data: { enabled: checked },
              },
            })
          }
        />
      ),
    },
    {
      title: CREDENTIALS.ACTIONS,
      dataIndex: "id",
      align: "right",
      width: 64,

      render: (id, record) => (
        <Space size={4}>
          <Popconfirm
            title={CREDENTIALS.ROTATE_TITLE}
            description={CREDENTIALS.ROTATE_BODY}
            okText={CREDENTIALS.ROTATE_CONFIRM}
            cancelText={CREDENTIALS.DELETE_CANCEL}
            okButtonProps={{ loading: rotating }}
            onConfirm={() => rotate({ variables: { where: { id } } })}
          >
            <Tooltip title={CREDENTIALS.ROTATE}>
              <span
                className="api-credential__rotate"
                role="button"
                tabIndex={0}
              >
                <MdAutorenew size={22} />
              </span>
            </Tooltip>
          </Popconfirm>
          <Popconfirm
            title={CREDENTIALS.DELETE_TITLE}
            description={CREDENTIALS.DELETE_BODY}
            okText={CREDENTIALS.DELETE_CONFIRM}
            cancelText={CREDENTIALS.DELETE_CANCEL}
            okButtonProps={{ danger: true, loading: removing }}
            onConfirm={() => remove({ variables: { where: { id } } })}
          >
            <Tooltip title={CREDENTIALS.DELETE}>
              <span
                className="api-credential__delete"
                role="button"
                tabIndex={0}
                aria-label={`${CREDENTIALS.DELETE} ${get(record, "label", "")}`}
              >
                <MdOutlineDeleteOutline size={22} />
              </span>
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!credentials.length) {
    return <Text type="secondary">{CREDENTIALS.EMPTY}</Text>;
  }

  return (
    <>
      <Modal
        open={Boolean(rotated)}
        onCancel={() => setRotated(null)}
        footer={null}
        destroyOnClose
        title={CREDENTIALS.ROTATE_TITLE}
      >
        {rotated && (
          <SecretFields secret={rotated} onDone={() => setRotated(null)} />
        )}
      </Modal>
      <Table
        dataSource={credentials}
        columns={columns}
        rowKey="id"
        pagination={false}
        className="table__wrapper"
        scroll={{ x: true }}
      />
    </>
  );
};

const ApiCredentialList = ({ organizationId }) => (
  <ApiCredentials View={CredentialsTable} NoData={CredentialsTable} />
);

export default ApiCredentialList;
